/**
 * Which round phases have already been announced, so a phase voice-over sounds
 * at most ONCE per round.
 *
 * The previous guard compared the latest event's type against the last type it
 * had shown — a bare string, with no round identity and no ordering. Anything
 * that moved the round state BACKWARDS therefore read as a fresh transition and
 * re-announced it: a Supabase refetch landing behind a socket event would drop
 * `race_starting`, the latest event would fall back to `race_ready`, and "place
 * your bets" would play again seconds after betting had closed.
 *
 * The store no longer lets a fetch shrink what we know (see
 * [[mergeFetchedIds]]), which removes the cause. This is the second line: even
 * if the round pointer moves backwards for some other reason, a phase that has
 * already been announced for a round is never announced again.
 */

/**
 * Ordering of the round lifecycle. `race_crashed` and `race_ended` share a rank
 * because they are the same stage for different game families — a game emits
 * one or the other, never both.
 */
const PHASE_RANK: Record<string, number> = {
  race_scheduled: 0,
  race_ready: 1,
  race_starting: 2,
  race_started: 3,
  race_crashed: 4,
  race_ended: 4,
  race_settled: 5
}

/** Rank of a lifecycle phase, or null for anything not part of it. */
export const phaseRank = (eventType: string): number | null =>
  eventType in PHASE_RANK ? PHASE_RANK[eventType]! : null

/**
 * How many rounds to remember. Comfortably more than the round list the widget
 * holds, and bounded so a long session cannot grow the map indefinitely.
 */
export const ANNOUNCED_HISTORY_LIMIT = 20

/**
 * Whether this event is a phase the given round has not announced yet.
 *
 * FORWARD-ONLY per round: re-delivering an earlier phase, or the same one
 * twice, is silent. An event outside the lifecycle is never announced.
 */
export const shouldAnnouncePhase = (
  raceId: string,
  eventType: string,
  announced: ReadonlyMap<string, number>
): boolean => {
  const rank = phaseRank(eventType)
  if (rank === null) return false

  const highest = announced.get(raceId)

  return highest === undefined || rank > highest
}

/**
 * Record an announced phase, evicting the oldest rounds past the limit.
 *
 * Mutates and returns the same map — it lives in a ref, deliberately not in
 * React state: `setState` is asynchronous, so two updates arriving in the same
 * commit would both read the stale value and both announce.
 */
export const rememberAnnouncedPhase = (
  raceId: string,
  eventType: string,
  announced: Map<string, number>
): Map<string, number> => {
  const rank = phaseRank(eventType)
  if (rank === null) return announced

  const highest = announced.get(raceId)
  if (highest !== undefined && rank <= highest) return announced

  // Re-insert so this round becomes the most recently touched key (Map
  // preserves insertion order, which is what the eviction below relies on).
  announced.delete(raceId)
  announced.set(raceId, rank)

  while (announced.size > ANNOUNCED_HISTORY_LIMIT) {
    const oldest = announced.keys().next()
    if (oldest.done) break
    announced.delete(oldest.value)
  }

  return announced
}
